import { useEffect, useState } from "react";
import { Mail, Building2, DollarSign, Briefcase, MessageSquare } from "lucide-react";
import { getSubmissions } from "@/lib/api";

export default function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSubmissions()
      .then((data) => setItems(data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-submissions">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Inbox ]</p>
      <h1 className="mt-3 font-display text-4xl md:text-5xl tracking-tight">Contact submissions.</h1>
      <p className="mt-2 text-zinc-400">{items.length} message{items.length === 1 ? "" : "s"} received.</p>

      <div className="mt-10 space-y-3">
        {loading ? (
          <div className="rounded-2xl glass border-white/[0.08] p-10 text-center text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl glass border-white/[0.08] p-10 text-center text-zinc-500 text-sm">
            No submissions yet.
          </div>
        ) : (
          items.map((s) => (
            <article
              key={s.id}
              data-testid={`submission-${s.id}`}
              className="rounded-2xl glass border-white/[0.08] p-6 hover:bg-white/[0.04] transition"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-cabinet font-medium text-lg">{s.name}</h3>
                  <a href={`mailto:${s.email}`} className="text-sm text-nexus-cyan hover:underline inline-flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" /> {s.email}
                  </a>
                </div>
                <span className="text-xs text-zinc-500 font-mono">
                  {new Date(s.created_at).toLocaleString()}
                </span>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mb-4 text-xs">
                {s.company && (
                  <Meta icon={Building2} label="Company" value={s.company} />
                )}
                {s.service && (
                  <Meta icon={Briefcase} label="Service" value={s.service} />
                )}
                {s.budget && (
                  <Meta icon={DollarSign} label="Budget" value={s.budget} />
                )}
              </div>

              <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4">
                <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-2">
                  <MessageSquare className="h-3.5 w-3.5" /> Message
                </div>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap">{s.message}</p>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] px-3 py-2">
      <div className="flex items-center gap-1 text-zinc-500 text-[10px] uppercase tracking-widest">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-sm mt-0.5">{value}</div>
    </div>
  );
}
